import React from 'react';
import {
  booleanOptions2,
  ConditionType,
  Filter,
  FilterOption,
  getCondition,
  getRelationalExpressionProps,
  isNullOperator,
  RelationsProps,
  Select,
  selectorWidth,
  StringConditionType,
} from '@fabric-ui/components';
import FilterInput from './components/FilterInput';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { useFilterConfig } from './FilterContext';
import { useGetValuesOptions } from './hooks/useGetValuesOptions';

const emptyRelationsProps: RelationsProps = {};

interface StringNumberTypeBoxProps {
  filter: Filter;
  setFilter: (filter: Filter) => void;
  typeCondition: ConditionType;
  options: FilterOption[];
  disabled?: boolean;
  relationsProps?: RelationsProps;
  resetOperatorOnChange?: boolean;
  filterInputProps?: Partial<React.ComponentProps<typeof FilterInput>>;
}
const FilterTypeBox: React.FC<StringNumberTypeBoxProps> = ({
  filter,
  setFilter,
  typeCondition,
  options,
  disabled = false,
  relationsProps = emptyRelationsProps,
  resetOperatorOnChange = false,
  filterInputProps,
}) => {
  const { size, isVertical, fullWidth } = useFilterConfig();
  const { fieldExtraOptions, supportParser, timeRange, readOnly, tokenSuggestions } = useExpressionBuilder();
  const compoundOperator = getCondition(filter, typeCondition, supportParser ? StringConditionType.EQUALS : undefined);
  const [operator] = compoundOperator.split('|');
  const { additionalSelectOptions = [], enableCaseSensitive = false, enableSide = false } = relationsProps;

  const innerExpression = filter.expression?.arrayCondition?.underlying || filter.expression;
  const fieldName = innerExpression?.fieldName;
  const valueOptions = fieldName ? fieldExtraOptions?.[fieldName]?.map(v => ({ value: v, title: v })) : undefined;

  const setValue = value => {
    const wasToken = resetOperatorOnChange;
    const firstOption = options[0]?.value ?? operator;
    const [firstBaseOperator, firstResolution] = firstOption.split('|');
    const effectiveOperator = wasToken ? firstBaseOperator : operator;
    const normalizedValue = valueOptions && effectiveOperator === 'in' && Array.isArray(value) ? { values: value } : value;
    const underlying = {
      fieldName: innerExpression?.fieldName,
      [typeCondition]: { [effectiveOperator]: normalizedValue },
      ...getRelationalExpressionProps({ operator: effectiveOperator, typeCondition, enableCaseSensitive, enableSide }),
    };
    const existingResolution = filter.expression?.arrayCondition?.resolution;
    const newResolution = wasToken && firstResolution ? firstResolution : existingResolution;
    return setFilter({
      ...filter,
      expression: filter.expression?.arrayCondition ? { arrayCondition: { underlying, resolution: newResolution } } : underlying,
    });
  };

  const rawValue = isNullOperator(operator) ? '' : innerExpression?.[typeCondition]?.[operator];
  const value =
    valueOptions && operator === 'in'
      ? !fieldName
        ? ''
        : rawValue && typeof rawValue === 'object' && Array.isArray(rawValue.values)
          ? rawValue.values
          : []
      : rawValue;

  const width = isVertical ? selectorWidth : fullWidth ? '100%' : 250;
  const minWidth = fullWidth && !isVertical ? 182 : undefined;
  const isInputDisabled = isNullOperator(operator) || !innerExpression?.fieldName || disabled;

  const inputProps = {
    readOnly,
    disabled: isInputDisabled,
    value,
    onChange: setValue,
    placeholder: isInputDisabled ? '' : 'Type Value',
    size,
  };

  const enableAutoCompleteValue =
    !!supportParser &&
    [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType) &&
    !!innerExpression?.fieldName;
  const { data: openSelectOptions, isLoading } = useGetValuesOptions({
    fieldName: innerExpression?.fieldName,
    enabled: enableAutoCompleteValue,
    timeRange,
  });
  return [ConditionType.bool, ConditionType.field /* , ConditionType.enum */].includes(typeCondition) ? (
    <Select
      {...inputProps}
      style={{ width, minWidth }}
      isRequired
      muiProps={{ disablePortal: true }}
      options={[
        ...(typeCondition === ConditionType.bool ? booleanOptions2 : additionalSelectOptions),
        ...(isNullOperator(operator) ? [{ title: '', value: '' }] : []),
      ]}
    />
  ) : (
    <FilterInput
      {...inputProps}
      inputWidth={width}
      inputMinWidth={minWidth}
      numberField={typeCondition === ConditionType.number}
      operator={compoundOperator}
      options={options}
      optionsUseQueryProps={enableAutoCompleteValue ? { options: openSelectOptions, isLoading } : undefined}
      valueOptions={valueOptions}
      skeletonLoading
      tokenSuggestions={tokenSuggestions}
      {...filterInputProps}
    />
  );
};

export default FilterTypeBox;
